import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useIsOverflown } from "hooks/useIsOverflown";
import { NavLink } from "react-router-dom";
import { LinkType, SubLinkType, SubMenu } from "types/sidebar";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    item: {
      margin: theme.spacing(1, 0),
    },
    listItem: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(3, 8),
      borderRadius: "6px",
      textDecoration: "none",
      color: "#6D6D6D",
      cursor: "pointer",
      border: "1px solid transparent",

      "&:hover": {
        backgroundColor: theme.white00,
        border: "1px solid #E5E5E5",
      },

      "&:active": {
        color: "#6D6D6D",
      },

      "& svg": {
        stroke: "#6D6D6D",
        width: 20,
        minWidth: ({ isDrawerOpen }: { isDrawerOpen?: boolean }) =>
          isDrawerOpen ? 20 : "unset",
        "& path": {
          strokeWidth: "1.5",
        },
      },
    },
    active: {
      backgroundColor: theme.white00,
      border: "1px solid #E5E5E5",
      color: "#0D0D0D",

      "& svg": {
        stroke: "#0D0D0D",
      },
    },
    square: {
      width: 48,
      height: 48,
      justifyContent: "center",
      padding: 0,
    },
    label: {
      fontSize: 14,
      textWrap: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      flex: 1,
      margin: theme.spacing(0, 3, 0, 8),
    },
    tooltip: {
      overflow: "hidden",
      position: "relative",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  }),
);

interface Props {
  details: LinkType;
  items?: SubLinkType[];
  isDrawerOpen?: boolean;
  setSubMenu?: (subMenu: SubMenu | null) => void;
}

export const Link = ({ details, items, isDrawerOpen, setSubMenu }: Props) => {
  const classes = useStyles({ isDrawerOpen });
  const { message } = useMessage();
  const { observeElement, isOverflown } = useIsOverflown();
  const isSubMenu = !details.link && items && setSubMenu;

  return (
    <li className={isDrawerOpen ? classes.item : ""}>
      <Tooltip
        text={isDrawerOpen ? "" : message({ id: details.label })}
        placement="right"
        disableHoverListener={isDrawerOpen}
      >
        <div>
          <NavLink
            data-sentry-unmask
            to={isSubMenu ? items[0].link : details.link || "#"}
            data-test-id={`side-nav-${details.label}-item`}
            className={({ isActive }) => `
                            ${classes.listItem} 
                            ${isActive && details.link ? classes.active : ""}
                            ${!isDrawerOpen ? classes.square : ""}
                          `}
            onClick={() => {
              if (isSubMenu) {
                return setSubMenu({
                  header: details.label,
                  links: items,
                });
              }

              if (setSubMenu) {
                setSubMenu(null);
              }
            }}
          >
            {details.icon}
            {isDrawerOpen && (
              <Tooltip
                disableHoverListener={!isOverflown}
                text={message({ id: details.label })}
                className={classes.tooltip}
                placement="right"
              >
                <div>
                  <Typography className={classes.label} ref={observeElement}>
                    {message({ id: details.label })}
                  </Typography>
                </div>
              </Tooltip>
            )}
            {isDrawerOpen && details.badge}
          </NavLink>
        </div>
      </Tooltip>
    </li>
  );
};
